/**
 * CustomerSuggestion 组件
 * 从 AZ_AI_MVR_H5/src/pages/OpsMate/components/CustomerSuggestion/index.tsx 迁移
 *
 * 核心逻辑已拆分至:
 * - hooks/opsmate/useSubmitMessage.ts  消息提交（首问+续问）
 * - SenderFooter.tsx                   底部工具栏
 */
import { Prompts, Sender, Suggestion } from "@ant-design/x";
import type { PromptsProps } from "@ant-design/x";
import { Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AiStarIcon from "@/assets/opsmate/a.svg";
import Icon1 from "@/assets/opsmate/icon_1.svg";
import Icon2 from "@/assets/opsmate/icon_2.svg";
import Icon3 from "@/assets/opsmate/icon_3.svg";
import Icon4 from "@/assets/opsmate/icon_4.svg";
import Icon5 from "@/assets/opsmate/icon_5.svg";
import Icon6 from "@/assets/opsmate/icon_6.svg";

import { useAuth } from "@/context/auth";
import { useOpsMateContext } from "@/contexts/useOpsMateContext";
import { isHistoryConversation } from "@/utils/conversation";
import { getRecommendedQuestionsFront } from "@/services/recommendedQuestion";
import {
  getAskScope,
  EMPTY_ASK_SCOPE,
  resolveEffectiveCategoryIds,
  resolveEffectiveStudySourceIds,
  resolveDefaultCategoryIds,
  resolveDefaultScopePills,
} from "@/services/opsmate/askScope";
import type { AskScope } from "@/services/opsmate/askScope";
import { useSubmitMessage, MAX_QUESTION_LENGTH } from "@/hooks/opsmate/useSubmitMessage";
import { canSubmitQuestionScope } from "@/hooks/opsmate/useSubmitMessage.scope";
import SenderFooter from "./SenderFooter";
import type {
  KBScope,
  SourceTypeOption,
  StudyOption,
} from "@/pages/opsmate/components/CustomerSuggestion/KBScopeSelector";

export default function CustomerSuggestion() {
  const { user: currentUser } = useAuth();

  // 截取名字：取第一个逗号/句号之后、第一个左括号之前的内容。
  // 例如 "Kuang, Kelvin (Microsoft)" → "Kelvin"，"Han, Louie" → "Louie"
  const displayName = useMemo(() => {
    const name = currentUser?.name ?? "";
    const match = name.match(/[,.]\s*(.+?)\s*(?:\(|$)/);
    return match ? match[1].trim() : name;
  }, [currentUser]);

  const {
    items,
    setItemsForConversation,
    refreshConversations,
    setSelectedKeys,
    selectedKeys,
    setDocsCollapsed,
    onlyUseSelectedSources,
    setOnlyUseSelectedSources,
    pendingConversationId,
    setPendingConversationId,
    migrateNewConversationSources,
    selectedSourcesCount,
    availableSourcesCount,
    selectedSourceGuidNames,
    setQuestionLibraryScope,
  } = useOpsMateContext();

  const { t } = useTranslation();

  const iconList = useMemo(() => [Icon1, Icon2, Icon3, Icon4, Icon5, Icon6], []);
  const defaultPromptItems = useMemo<PromptsProps["items"]>(
    () => [
      {
        key: "1",
        icon: <img src={Icon1} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        label: t("opsmate.suggestion.q1"),
      },
      {
        key: "2",
        icon: <img src={Icon2} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        label: t("opsmate.suggestion.q2"),
      },
      {
        key: "3",
        icon: <img src={Icon3} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        label: t("opsmate.suggestion.q3"),
      },
      {
        key: "4",
        icon: <img src={Icon4} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        label: t("opsmate.suggestion.q4"),
      },
      {
        key: "5",
        icon: <img src={Icon5} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        label: t("opsmate.suggestion.q5"),
      },
      {
        key: "6",
        icon: <img src={Icon6} alt="" aria-hidden style={{ width: 20, height: 20 }} />,
        label: t("opsmate.suggestion.q6"),
      },
    ],
    [t],
  );
  // 初始为空，避免在后端推荐问题返回前把内置默认项当作 placeholder/cloak 闪现。
  // 仅当后端 /api/project/recommendedquestion/front 返回空数据时，才回退到 defaultPromptItems。
  const [promptItems, setPromptItems] = useState<PromptsProps["items"]>([]);
  const [kbScopes, setKbScopes] = useState<KBScope[]>([]);
  const [selectedSourceTypes, setSelectedSourceTypes] = useState<string[] | null>([]);
  const [selectedStudies, setSelectedStudies] = useState<string[] | null>([]);

  // 可选项完全由后端 AskScope 按 StudyCraAssignment / SourcePathMembers 裁定；
  // 前端角色缓存不能替代权限判断。
  const [askScope, setAskScope] = useState<AskScope>(EMPTY_ASK_SCOPE);

  const visibleScopes = useMemo<KBScope[]>(() => {
    const scopes: KBScope[] = [];
    if (askScope.generalDocs.visible) scopes.push("general");
    if (askScope.studySource.visible) scopes.push("myStudies");
    if (askScope.personalDocs.visible) scopes.push("personal");
    return scopes;
  }, [askScope]);

  const sourceTypes = useMemo<SourceTypeOption[]>(
    () => askScope.generalDocs.categories.map((c) => ({ id: c.id, name: c.name })),
    [askScope],
  );
  const availableStudies = useMemo<StudyOption[]>(
    () =>
      askScope.studySource.studies.map((study) => ({
        id: study.id,
        code: study.code,
        name: study.name,
        studySourceIds: study.studySourceIds,
      })),
    [askScope],
  );

  // 权限配置刷新后移除已失效的历史选择，避免旧状态把越权分类带进请求。
  // sourceTypes 为空通常是刷新/加载中的瞬时态，此时不清理，避免误删仍然有效的已选项；
  // 越权防护已由 resolveEffectiveCategoryIds 与服务端白名单兜底，残留的旧状态无法扩权。
  useEffect(() => {
    if (sourceTypes.length === 0) return;
    const authorizedIds = new Set(sourceTypes.map((item) => item.id));
    setSelectedSourceTypes((current) => {
      if (current === null) return current;
      const next = current.filter((id) => authorizedIds.has(id));
      return next.length === current.length && next.every((id, index) => id === current[index])
        ? current
        : next;
    });
  }, [sourceTypes]);

  useEffect(() => {
    if (availableStudies.length === 0) return;
    const authorizedIds = new Set(availableStudies.map((item) => item.id));
    setSelectedStudies((current) => {
      if (current === null) return current;
      const next = current.filter((id) => authorizedIds.has(id));
      return next.length === current.length && next.every((id, index) => id === current[index])
        ? current
        : next;
    });
  }, [availableStudies]);

  const effectiveDocCategoryIds = useMemo<string[]>(
    () =>
      resolveEffectiveCategoryIds(
        kbScopes.includes("general"),
        selectedSourceTypes,
        sourceTypes.map((item) => item.id),
      ),
    [kbScopes, selectedSourceTypes, sourceTypes],
  );

  const effectiveStudySourceIds = useMemo<string[]>(
    () =>
      resolveEffectiveStudySourceIds(
        kbScopes.includes("myStudies"),
        selectedStudies,
        availableStudies,
      ),
    [availableStudies, kbScopes, selectedStudies],
  );

  useEffect(() => {
    setQuestionLibraryScope({
      docCategoryIds: effectiveDocCategoryIds,
      studySourceIds: effectiveStudySourceIds,
      includePersonalDocs: kbScopes.includes("personal"),
    });
  }, [effectiveDocCategoryIds, effectiveStudySourceIds, kbScopes, setQuestionLibraryScope]);

  // ── 提交消息 hook ──
  const {
    value,
    setValue,
    submitting,
    conversationId,
    setConversationId,
    inputRef,
    stopGeneration,
    handleSubmit,
  } = useSubmitMessage({
    items,
    setItemsForConversation,
    refreshConversations,
    setSelectedKeys,
    selectedKeys,
    onlyUseSelectedSources,
    pendingConversationId,
    setPendingConversationId,
    migrateNewConversationSources,
    selectedSourceGuidNames,
    docCategoryIds: effectiveDocCategoryIds,
    studySourceIds: effectiveStudySourceIds,
    includePersonalDocs: kbScopes.includes("personal"),
  });

  // ── 加载提问范围配置（胶囊的可见性 / 可选项 / 默认勾选）──
  useEffect(() => {
    let cancelled = false;
    getAskScope()
      .then((scope) => {
        if (cancelled) return;
        setAskScope(scope);

        setKbScopes(resolveDefaultScopePills(scope));

        setSelectedSourceTypes(resolveDefaultCategoryIds(scope.generalDocs));
      })
      .catch((e) => {
        console.error("getAskScope 调用失败:", e);
        if (!cancelled) {
          setAskScope(EMPTY_ASK_SCOPE);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── 加载推荐问题 ──
  useEffect(() => {
    let cancelled = false;
    const loadRecommendedQuestions = async () => {
      try {
        const resp: any = await getRecommendedQuestionsFront();
        const list: any[] = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.Data)
            ? resp.Data
            : Array.isArray(resp?.Entity)
              ? resp.Entity
              : [];

        const normalized = list
          .map((row: any) => ({
            id: row?.ID ?? row?.Id ?? row?.id,
            question: typeof row?.Question === "string" ? row.Question.trim() : "",
            displayOrder: Number(row?.DisplayOrder ?? 0),
          }))
          .filter((row) => !!row.question)
          .sort((a, b) => a.displayOrder - b.displayOrder);

        if (cancelled) return;

        // 后端返回空数据时，才使用内置默认问题作为缺省值
        if (normalized.length === 0) {
          setPromptItems(defaultPromptItems);
          return;
        }

        const nextPromptItems: PromptsProps["items"] = normalized.map((row, index) => ({
          key: String(row.id ?? `recommend-${index + 1}`),
          icon: (
            <img
              src={iconList[index % iconList.length]}
              alt=""
              aria-hidden
              style={{ width: 20, height: 20 }}
            />
          ),
          label: row.question,
        }));

        setPromptItems(nextPromptItems);
      } catch (e) {
        console.error("getRecommendedQuestionsFront 调用失败:", e);
        if (!cancelled) setPromptItems(defaultPromptItems);
      }
    };
    loadRecommendedQuestions();
    return () => {
      cancelled = true;
    };
  }, [iconList, defaultPromptItems]);

  // ── 焦点管理 ──
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        const textarea = inputRef.current.querySelector("textarea");
        if (textarea) textarea.focus();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [inputRef]);

  useEffect(() => {
    if (isHistoryConversation(selectedKeys)) {
      setConversationId(String(selectedKeys));
      return;
    }
    if (selectedKeys === "add" && conversationId) {
      setConversationId(null);
    }
  }, [selectedKeys, conversationId, setConversationId]);

  useEffect(() => {
    if (items.length > 0) {
      const timer = setTimeout(() => {
        if (inputRef.current) {
          const textarea = inputRef.current.querySelector("textarea");
          if (textarea) textarea.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    return;
  }, [items.length, inputRef]);

  const handleStopGeneration = () => {
    stopGeneration();
  };

  const isNewAsk = !conversationId || selectedKeys === "add";
  const showSourcesCount = !isNewAsk;
  const hasUsableSelectedSources = canSubmitQuestionScope(true, selectedSourceGuidNames);

  return (
    <div
      style={{
        maxWidth: isNewAsk ? "56vw" : "80vw",
        margin: "0 auto",
        width: "100%",
        alignItems: "center",
        display: "flex",
      }}
    >
      <div style={{ width: "100%" }}>
        {isNewAsk ? (
          <>
            <Typography.Title
              level={2}
              style={{
                marginBottom: 24,
                textAlign: "center",
                fontWeight: 600,
                fontSize: 36,
                background: "linear-gradient(to right, #830051 0%, #EFAB00 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {t("opsmate.suggestion.greeting", { name: displayName })}
            </Typography.Title>
            <div className="flex justify-center items-center gap-2 mb-7">
              <a
                href="https://azcollaboration.sharepoint.com/sites/TSP1060/SitePages/OpsMate%20Library.aspx"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 bg-gray-50 rounded-full px-3 py-1.5 az-link cursor-pointer hover:bg-gray-100 transition-colors no-underline"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shield-keyhole-line text-sm" style={{ color: "#830051" }}></i>
                </div>
                <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
                  Internal Knowledge Only
                </span>
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  · Private &amp; Secure
                </span>
              </a>
              <div className="flex items-center gap-1.5 bg-gray-50 rounded-full px-3 py-1.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-article-line text-sm" style={{ color: "#830051" }}></i>
                </div>
                <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
                  Source-cited Responses
                </span>
                <span className="text-xs text-gray-400 whitespace-nowrap">· Always Traceable</span>
              </div>
            </div>
          </>
        ) : null}
        <Suggestion items={[{ label: "Write a report", value: "report" }]}>
          {({ onTrigger, onKeyDown }) => {
            return (
              <div ref={inputRef}>
                <Sender
                  value={value}
                  onChange={(nextVal) => {
                    if (nextVal === "/") {
                      onTrigger();
                    } else if (!nextVal) {
                      onTrigger(false);
                    }
                    setValue(nextVal);
                  }}
                  onKeyDown={(e) => {
                    // 阻止冒泡到 Suggestion(Cascader) 外层，否则空格/回车会被
                    // @rc-component/select 的 BaseSelect 调用 preventDefault 吃掉
                    e.stopPropagation();

                    // 回车直接提交；Shift+Enter 换行
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (e.nativeEvent.isComposing || (e as any).keyCode === 229) {
                        // 中文/日文输入法组合中，忽略
                        return;
                      }
                      if (submitting) return;
                      if (value.trim().length === 0) return;
                      if (!canSubmitQuestionScope(onlyUseSelectedSources, selectedSourceGuidNames))
                        return;
                      // 超过最大字符数时不允许提交（与发送按钮置灰逻辑保持一致）
                      if (value.length > MAX_QUESTION_LENGTH) return;
                      handleSubmit();
                      return;
                    }
                    onKeyDown(e);
                  }}
                  placeholder={
                    isNewAsk
                      ? "Ask about company policies, SOPs, processes, or internal guidelines. Quality related questions must be reviewed by CQM Helpdesk."
                      : t("opsmate.suggestion.inputPlaceholder")
                  }
                  autoSize={isNewAsk ? { minRows: 4, maxRows: 6 } : { minRows: 2, maxRows: 6 }}
                  prefix={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <img
                        src={AiStarIcon}
                        alt="AI"
                        style={{ width: 20, height: 20, marginTop: 3 }}
                      />
                    </div>
                  }
                  styles={{
                    ...(isNewAsk
                      ? {
                          prefix: { alignSelf: "flex-start", marginTop: 4, marginInlineEnd: 4 },
                          root: {
                            border: "none",
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
                            borderRadius: 12,
                            background: "#fff",
                            padding: 4,
                          },
                          input: {
                            border: "none",
                            boxShadow: "none",
                            outline: "none",
                            alignSelf: "flex-start",
                            fontSize: 14,
                          },
                          content: { border: "none" },
                        }
                      : {
                          root: {
                            border: "none",
                            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.06)",
                            borderRadius: 12,
                            background: "#fff",
                          },
                          content: { alignItems: "flex-start", paddingBlock: 8 },
                          prefix: { alignSelf: "flex-start", marginTop: 2, marginInlineEnd: 8 },
                          input: { alignSelf: "flex-start", fontSize: 14 },
                        }),
                  }}
                  suffix={false}
                  footer={() => (
                    <SenderFooter
                      value={value}
                      submitting={submitting}
                      isInitPage={isNewAsk}
                      kbScopes={kbScopes}
                      onScopesChange={setKbScopes}
                      visibleScopes={visibleScopes}
                      sourceTypes={sourceTypes}
                      selectedSourceTypes={selectedSourceTypes}
                      onSourceTypesChange={setSelectedSourceTypes}
                      availableStudies={availableStudies}
                      selectedStudies={selectedStudies}
                      onStudiesChange={setSelectedStudies}
                      showSourcesCount={showSourcesCount}
                      selectedSourcesCount={selectedSourcesCount}
                      availableSourcesCount={availableSourcesCount}
                      hasSelectedSources={hasUsableSelectedSources}
                      onlyUseSelectedSources={onlyUseSelectedSources}
                      onOnlyUseSelectedSourcesChange={setOnlyUseSelectedSources}
                      setDocsCollapsed={setDocsCollapsed}
                      onSubmit={handleSubmit}
                      onStopGeneration={handleStopGeneration}
                    />
                  )}
                />
              </div>
            );
          }}
        </Suggestion>
        {isNewAsk ? (
          <div style={{ marginTop: 18 }}>
            <Prompts
              items={promptItems}
              onItemClick={({ data }) => {
                const nextVal = typeof data?.label === "string" ? data.label : "";
                if (!nextVal) return;
                setValue(nextVal);
                window.setTimeout(() => {
                  const textarea = inputRef.current?.querySelector?.("textarea");
                  textarea?.focus?.();
                }, 0);
              }}
              wrap
              styles={{
                item: {
                  flex: "none",
                  width: "calc(50% - 6px)",
                  background: "rgba(131, 0, 81, 0.10)",
                  borderRadius: 8,
                  border: "1px solid #FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  paddingInline: 12,
                },
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
